import React, { useState, useEffect, useCallback } from "react";
import ExcelJS from 'exceljs';
import { Grid as GridLoading } from 'react-loader-spinner';
import Highlighter from "react-highlight-words";
import { styled } from '@mui/material/styles';
import { theme } from '../../theme';
import { OpenAIClient, AzureKeyCredential } from "@azure/openai";
import config from '../../config';

// Import MUI elements
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from "@mui/material/IconButton";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import SquareIcon from '@mui/icons-material/Square';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from "@mui/material/TextField";
import Typography from '@mui/material/Typography';

// Import framework to manage datepicker
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Import icons
import AssistantPhotoIcon from '@mui/icons-material/AssistantPhoto';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BusinessIcon from '@mui/icons-material/Business';
import CallIcon from '@mui/icons-material/Call';
import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from '@mui/icons-material/Send';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { Divider } from "@mui/material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        fontSize: 13,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const ModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 900,
    height: 670,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    p: 4,
    overflow: 'scroll',
    display: 'block'
};

const TranscriptModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1250,
    height: 750,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    p: 4,
    overflow: 'scroll',
    display: 'block'
};

function AgentEffectivenessSearch({ parentToChild }: { parentToChild: any }) {
    const { startDate, endDate, mode } = parentToChild;

    const [insightsData, setInsightsData] = useState<any>(null);
    const [insightsModalOpen, setInsightsModalOpen] = useState<boolean>(false);
    const [isInsightsDataLoading, setIsInsightsDataLoading] = useState<boolean>(true);
    const [data, setData] = useState<any>();
    const [error, setError] = useState<any>(null);

    const [inputValue, setInputValue] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [selectedTranscript, setSelectedTranscript] = useState<[string, any[], string, string]>(['', [], '', '']);

    const [transcriptIds, setTranscriptIds] = useState<any[]>([]);
    const [groupInsightsData, setGroupInsightsData] = useState<any>([]);
    const [isGroupInsightsLoading, setIsGroupInsightsLoading] = useState<boolean>(true);
    const [groupInsightsModalOpen, setGroupInsightsModalOpen] = useState<boolean>(false);
    const [competitorMentionedData, setCompetitorMentionedData] = useState<any>(null);
    const [promotionsMentionedData, setPromotionsMentionedData] = useState<any>(null);
    const [customerReactionData, setCustomerReactionData] = useState<any>(null);
    const [generalSummaryData, setGeneralSummaryData] = useState<any>(null);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [highlightsData, setHighlightsData] = useState<any>([]);

    // Variables for sending a report
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [subject, setSubject] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [comments, setComments] = useState<string>('');
    const [frequency, setFrequency] = useState<string>('Daily');
    const [recurrences, setRecurrences] = useState<string>('1');
    const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
    const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
    const [excelFile, setExcelFile] = useState<any | null>(null);
    const Today = dayjs();

    const connectAzureOpenAI = async (insightsParamsString: string) => {
        setIsInsightsDataLoading(true);
        const endpoint = config.azureOpenAI.chatEndpoint;
        const apiKey = config.azureOpenAI.chatKey;
        const deploymentId = config.azureOpenAI.chatDeployment;

        if (!endpoint || !apiKey) {
            throw new Error('Azure OpenAI chat credentials are not configured in environment variables.');
        }

        const client = new OpenAIClient(
            endpoint,
            new AzureKeyCredential(apiKey)
        );

        const messages = [
            { role: "system",
                content: `
                    You are a highly advanced AI working for a bank who is analyzing transcripts of calls between customers of the bank and call center agents also working for the bank. "
                    "You will be provided a string that formatted as a JSON object that contains 2 values, (1)transcript and (2)promotion_sentence variable that shows whether promotion is mentioned in the transcript. You need to use your brain and NLP techniques to parse these transcripts for very specific features of the conversations that they contain. "
                    "You should provide the following parsed features for each transcript in a JSON format. The possible values that you can return for each feature are listed after the name of the feature including further instructions where applicable. "
                    "These Features include:\n\n"
                    "**Product**: Credit Card, Debit Card, Auto Loan, Mortgage Loan, Savings Account, Checking Account, Personal Loan, Investment Services"
                    "**Reason**: Business Credit Card"
                    "Technical Assistance, and Account Management". Specifically for product Mortgage Loan, the reasons are "Shopping for rate", "Preapproval", "Rate lock", "Low appraisal", "Not qualified for requested amount", "Missing documents".
                    "Account Opening Assistance, Mobile App Issues, Account Closure or Upgrade"
                    "**Call Type**: Agent Outbound"
                    "**Promotion Mentioned**: Only when the given promotion_sentence variable is not 'NULL', extract and summarize from the variable what specific promotion, such as discounts, was offered to customer in one sentence. If promotion_sentence variable is 'NULL', please write 'No promotion is mentioned'."
                    "**Summary**: generate a summary of the call using 200 words or less, the summary should capture all the important points of the call and include how the call ended good or bad and whether or not the issue was resolved"
                    "**Call Sentiment**: Summarize the sentiment of the call in three words or less, varying from very frustrated to very satisfied. For example, if the call is not resolved and the customer asks to speak to the manager"
                    "or clearly express anger then summarize the sentiment of the call as "very frustrated". If the call goes well and the customer clearly expresses happiness then summarize the call as "very satisfied". If the customer does not express much anger or happiness then summarize the sentiment of the call as "neutral"\n\n"
                    "**Next Steps**: If the call was not resolved then provide the next steps that the agent should take to resolve the issue. If the call was resolved then return "No further action required".\n\n"
                    "That is all you must return, now find examples below of what is expected of you:\n\n"
                    "The following is a real transcript with an example of the expected result you will produce after parsing the transcript:\n\n"
                    "Agent: Hello, this is Sarah from Prosperity Bank. May I speak with Mr. Thompson, please?

                    Customer: This is John Thompson speaking.
                    
                    Agent: Good afternoon, Mr. Thompson! I'm calling regarding your small business credit card with us. We've noticed that there hasn't been much activity on your card in the past few months, and we wanted to check in to see if there's any particular reason for this change.
                    
                    Customer: Oh, hi Sarah. Yes, I've been meaning to use it, but I've run into some issues lately.
                    
                    Agent: I'm sorry to hear that. Can I ask you if you use the card at all of the merchants and vendors that you work with and if they take our card as a payment method?
                    
                    Customer: Actually, that's part of the problem. I cant use it at GHF Merchant because they do not accept your card as payment.
                    
                    Agent: I see. That must be quite inconvenient for you. We value your business, Mr. Thompson, and we would like to make sure you can use your credit card with ease for all your transactions. To help with this, Prosperity Bank could offer you an exclusive discount on processing fees for the next six months, which might also incentivize some of your vendors, like GHF Merchant, to start accepting our card.
                    
                    Customer: That does sound helpful. What kind of discount are we talking about?
                    
                    Agent: We can offer you a 15% discount on the processing fees for your business credit card transactions. This offer would be valid for the next six months, and we hope it would not only benefit you but also encourage GHF Merchant to reconsider their payment options.
                    
                    Customer: Well, that would certainly make a difference. I'll talk to GHF Merchant about it. Do I need to do anything to activate this discount?
                    
                    Agent: No, Mr. Thompson, I'll take care of everything on our end. You'll see the discounted rates on your upcoming statements. If you have any questions or need further assistance, please don't hesitate to reach out to us.
                    
                    Customer: Thank you, Sarah. I appreciate the help.
                    
                    Agent: It's my pleasure, Mr. Thompson. We're here to support your business. If there's anything else we can assist you with, please let us know. Have a great day!
                    
                    Customer: You too, bye.
                    
                    Agent: Goodbye, Mr. Thompson."
                    "***that is the end of the transcript, expected result from you is the following, but in JSON format as mentioned:\n\n"
                    "Product: Business Credit Card"
                    "Reason: Low card activity"
                    "Call_Type: Agent Outbound"
                    "Promotion_Mentioned: Agent offered 15% discount on the processing fees for customer's business credit card transactions."
                    "Summary: The agent contacts Mr. Thompson regarding the low activity on his small business credit card. Mr. Thompson explains that he has been unable to use the card at GHF Merchant, leading to the decreased activity. The agent offers a 15% discount on processing fees for the next six months to incentivize vendors like GHF Merchant to accept the bank's card. Mr. Thompson appreciates the offer and agrees to discuss it with GHF Merchant. The agent assures him that no action is required from his end and that the discounted rates will reflect on his statements. The call ends on a positive note with the agent offering further assistance if needed."
                    "Call_Sentiment:Satisfied"
                    "Next_Steps: No further action required"***
                `
            },
            { 
                role: "user",
                content: insightsParamsString
            },
        ];

        // console.log(`Messages: ${messages.map((m) => m.content).join("\n")}`);

        const events = await client.streamChatCompletions(deploymentId, messages, { maxTokens: 800, temperature: 0.2, responseFormat: {"type": "json_object"}});
        // console.log(events);
        let chatbotResponse = "";

        for await (const event of events) {
            for (const choice of event.choices) {
                const delta = choice.delta?.content;
                if (delta !== undefined) {
                    chatbotResponse += delta;
                }
            }
        }

        // console.log(`${chatbotResponse}`);
        setInsightsData(JSON.parse(chatbotResponse));
        setIsInsightsDataLoading(false);
    };

    // console.log(insightsData);

    const getTranscriptIds = () => {
        const ids = data.map((row: any) => row['Transcript ID']);
        setTranscriptIds(ids);
    }

    // Get group insights data
    useEffect(() => {
        setIsGroupInsightsLoading(true);
        // console.log(transcriptIds);
        let controller = new AbortController();
        let signal = controller.signal;

        const fetchData = async () => {
            try {
                const response = await fetch(`/get_group_insights_agent_eff`, {
                    signal,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        transcript_ids: transcriptIds,
                    }),
                });
                
                const result = await response.json();
                // console.log(result);
                const resultArray = [Object.values(await result)];
                setGroupInsightsData(resultArray);

                // const competitorMentioned = Object.keys(await result).find(key => key.toLowerCase() === 'competitor_mentions_on_these_calls');
                // const promotionsMentioned = Object.keys(await result).find(key => key.toLowerCase() === 'promotions_mentioned');

                const competitorMentioned = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'competitor_mentions_on_these_calls')?.[1];
                const promotionsMentioned = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'promotions_mentioned')?.[1];
                const customerReaction = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'customer_reactions_to_promotions')?.[1];
                const generalSummary = Object.entries(result).find(([key, value]) => key.toLowerCase() === 'general_summary')?.[1];

                // const competitorMentionedArray = (competitorMentioned && competitorMentioned) ? await result[competitorMentioned] : null;
                // const promotionsMentionedArray = (promotionsMentioned && promotionsMentioned !== 'No data available') ? await result[promotionsMentioned] : null;
                
                // console.log(competitorMentioned);
                // console.log(promotionsMentioned);
                // console.log(customerReaction);
                // console.log(generalSummary);

                setCompetitorMentionedData(competitorMentioned);
                setPromotionsMentionedData(promotionsMentioned);
                setCustomerReactionData(customerReaction);
                setGeneralSummaryData(generalSummary);
            } catch (error: any) {
                if (error.name === 'AbortError') {
                    // console.log('Request aborted');
                } else {
                    console.error('Error fetching data:', error);
                }
            }
        };

        if (transcriptIds.length > 0) {
            fetchData()
                .then(() => {
                    setIsGroupInsightsLoading(false);
            })
                .catch(error => console.error('Error in useEffect:', error));
        }

        return () => {
            controller.abort();
            controller = new AbortController();
        };
        // eslint-disable-next-line
    }, [transcriptIds]);

    // console.log(groupInsightsData);

    useEffect(() => {
        if (rangeStartDate && rangeEndDate && rangeStartDate.isAfter(rangeEndDate, 'day')) {
            setRangeStartDate(rangeEndDate);
        }
    }, [rangeStartDate, rangeEndDate]);

    // Handle excel file export
    const handleExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet 1');

        // Add headers
        const headers = Object.keys(data[0]);
        worksheet.addRow(headers);

        // Add data rows
        data.forEach((row: any) => {
            const rowData: any[] = []; // Explicitly define the type of rowData
            Object.values(row).forEach((cell) => {
                rowData.push(cell);
            });
            worksheet.addRow(rowData);
        });

        const excelBuffer = await workbook.xlsx.writeBuffer();

        setExcelFile(excelBuffer);
    };

    const handlePreview = () => {
        const blob = new Blob([excelFile], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'preview.xlsx';
        a.click();

        URL.revokeObjectURL(url);
    };

    const fetchData = useCallback(async (data: any) => {
        setIsLoading(true);
        try {
            const response = await fetch(`/search_agent_effec`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    keyword: data,
                }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();
            setData(result);
            setIsLoading(false);
        } catch (err) {
            setError(err || 'Something went wrong');
        }
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        fetchData(inputValue);
    }

    const handleOpen = (row: any) => {

        // console.log(row);

        let cleanTranscript = row['Transcript'].replace(/\*/g, '');

        let agentIndex = cleanTranscript.indexOf("Agent:");

        let finalTranscript
        if (agentIndex !== -1) {
            finalTranscript = cleanTranscript.substring(agentIndex);
        } else {
            finalTranscript = cleanTranscript;
        }

        const transcriptArray = finalTranscript.split(/(?=Agent(\s[A-Za-z]+)?:|Customer:)/)
        .filter((segment: string) => segment !== undefined && segment.trim().split(/\s+/).length > 1) // Filter out undefined segments
        .map((segment: string, index: number) => {
            if (index % 2 === 0) {
                return segment.trim();
            } else {
                return `\n${segment}`;
            }
        });
    
        setSelectedTranscript([row['TranscriptID'], transcriptArray, row['Transcript'], row['Agent Suggests Promotion Sentence']]);

        // Set highlights data
        const agentAlternativePaymentsArray = [];
        const agentAlternativePaymentPromotionArray = [];
        const agentPromotionArray = [];
        const agentCompetitorUsageArray = [];
        const agentVendorAcceptanceArray = [];
        const agentSpendProfileArray = [];
        const agentDataHistoryArray = [];
        const agentRateMatchArray = [];

        agentAlternativePaymentsArray.push(
            row['Agent Asks About Alternative Payments Sentence'] !== 'NULL'
                ? row['Agent Asks About Alternative Payments Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );
        
        agentAlternativePaymentPromotionArray.push(
            row['Agent Suggests Alternative Payment Promotion Sentence'] !== 'NULL'
                ? row['Agent Suggests Alternative Payment Promotion Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );
        
        agentPromotionArray.push(
            row['Agent Suggests Promotion Sentence'] !== 'NULL'
                ? row['Agent Suggests Promotion Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );
        
        agentCompetitorUsageArray.push(
            row['Agent Asks About Competitor Usage Sentence'] !== 'NULL'
                ? row['Agent Asks About Competitor Usage Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );
        
        agentVendorAcceptanceArray.push(
            row['Agent Asks About Vendor Acceptance Sentence'] !== 'NULL'
                ? row['Agent Asks About Vendor Acceptance Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );
        
        agentSpendProfileArray.push(
            row['Agent Discusses Spend Profile Sentence'] !== 'NULL'
                ? row['Agent Discusses Spend Profile Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );
        
        agentDataHistoryArray.push(
            row['Agent Points Out Data History Sentence'] !== 'NULL'
                ? row['Agent Points Out Data History Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );
        
        agentRateMatchArray.push(
            row['Agent Suggests Rate Match Sentence'] !== ''
                ? row['Agent Suggests Rate Match Sentence']
                      .split('\\n')[0]
                      .replace(/^Agent: /, '')
                : ''
        );

        setHighlightsData([agentAlternativePaymentsArray, agentAlternativePaymentPromotionArray, agentPromotionArray, agentCompetitorUsageArray, agentVendorAcceptanceArray, agentSpendProfileArray, agentDataHistoryArray, agentRateMatchArray]);
        
        setOpen(true);
    };

    // console.log(highlightsData);

    const Highlight = ({ children, highlightIndex }: { children: React.ReactNode, highlightIndex: number }) => {
        const colors = [
            '#ffe404', // Alternative_Payments_Sentence
            '#ffe404', // Alternative_Payments_Sentence_Promotion
            '#b7ad6c', // Promotion
            '#907c1c', // Competitor_Usage_Sentence
            '#c4c4cd', // Vendor_Acceptance_Sentence
            mode === 'light' ? '#2e2e38' : 'white', // Spend_Profile_Sentence
            mode === 'light' ? '#2e2e38' : 'white', // Data_History_Sentence
            mode === 'light' ? '#2e2e38' : 'white', // Agent_Rate_Match_Sentence
        ];
        const fontColors = [
            'black', // Alternative_Payments_Sentence
            'black', // Alternative_Payments_Sentence_Promotion
            'black', // Promotion
            'white', // Competitor_Usage_Sentence
            'white', // Vendor_Acceptance_Sentence
            mode === 'light' ? 'white' : 'black', // Spend_Profile_Sentence
            mode === 'light' ? 'white' : 'black', // Data_History_Sentence
            mode === 'light' ? 'white' : 'black', // Agent_Rate_Match_Sentence
        ];
        const color = colors[highlightIndex % colors.length];
        const fontColor = fontColors[highlightIndex % fontColors.length];
        return <span style={{ backgroundColor: color, fontFamily: theme.fonts.primary, color: fontColor }}>{children}</span>;
    };

    // console.log(highlightsData);

    const handleClose = () => {
        setOpen(false);
        setExcelFile(null);
    };

    const handleInsightsButtonClick = (transcript: string, promotion_sentence: string) => {
        const insightsParams = {
            transcript: transcript,
            promotion_sentence: promotion_sentence,
        }
        // console.log(insightsParams)
        const insightsParamsString = JSON.stringify(insightsParams);
        connectAzureOpenAI(insightsParamsString);
        setOpen(false);
        setInsightsModalOpen(true);
    };

    // Send report button modal
    const handleSendReport = () => {
        //console.log('Sending report:', { email, comments });
        setIsModalOpen(false);
    };
    
    const openModal = () => {
        setIsModalOpen(true);
    };
    
    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Insights buttons modal
    const handleCloseInsightsModal = () => setInsightsModalOpen(false);

    const handleCloseGroupInsightsModal = () => setGroupInsightsModalOpen(false);

    useEffect(() => {
        var init = '';
        fetchData(init);
    }, [fetchData, startDate, endDate]);

    error !== null && console.log(error);
    
    return (
        <div className="ops-search-wrapper">
            <div className="exec-search">
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                >
                    <TextField className="search-form" 
                    id="outlined-basic" 
                    label="Type your text here" 
                    variant="outlined" 
                    type="text" 
                    value={inputValue} 
                    onChange={handleInputChange} 
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleSubmit(e);
                        }
                    }}
                    />
                    <IconButton
                    size="small" 
                    onClick={(e) => {
                        handleSubmit(e);
                    }}>
                        <SearchIcon fontSize="inherit" />
                    </IconButton>
                </Box>
                <div className="ops-search-buttons">
                    <Button className="search-button" variant="contained" endIcon={<SendIcon />} onClick={() => {
                        handleExport();
                        openModal();
                    }}>
                        Send Report
                    </Button>
                    {data && data.length > 0 && (
                        <Button className="search-button" variant="contained" endIcon={<AutoAwesomeIcon />} 
                            onClick={() => {
                                getTranscriptIds();
                                setGroupInsightsModalOpen(true);
                            }}
                            >
                            AI Group Insights
                        </Button>
                    )}
                </div>
            </div>
            {data && !isLoading && data.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 550 }}>
                    <Table sx={{ minWidth: 500 }} stickyHeader aria-label="sticky table" id="myTable">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell className="table-header" align="center">Transcript ID</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Customer ID</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Call Type</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Call Reason</StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Transcript <span style={{fontSize: 10}}>(Click to view)</span></StyledTableCell>
                                <StyledTableCell className="table-header" align="center">Additional Insights</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((row: any, index: number) => (
                                <StyledTableRow key={row['Transcript ID']}>
                                    <StyledTableCell style={{ width: '12%' }} align="center">{row['Transcript ID']}</StyledTableCell>
                                    <StyledTableCell style={{ width: '10%' }} align="center">C{index*index+10}</StyledTableCell>
                                    <StyledTableCell style={{ width: '15%' }} align="center">Agent Outbound</StyledTableCell>
                                    <StyledTableCell style={{ width: '15%' }} align="center">Business Credit Card</StyledTableCell>
                                    <StyledTableCell style={{ width: '30%' }} align="center">
                                        <div className="table-transcript-link" onClick={() => handleOpen(row)}>
                                            {row['Transcript'].substr(0, 200) + '...'}
                                        </div>
                                    </StyledTableCell>
                                    <StyledTableCell style={{ width: '13%' }} align="center">
                                        <Button className="modal-btn" variant="contained" endIcon={<AutoAwesomeIcon />} onClick={() => {handleInsightsButtonClick(row['Transcript'], row['Agent Suggests Promotion Sentence'])}}>
                                            AI Insights
                                        </Button>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : isLoading ? (
                <div className="search-loading-area">
                    <Skeleton width="100%" height="200px" />
                    <span className="centered-text">
                        <GridLoading
                        visible={true}
                        height="15"
                        width="15"
                        color={mode === 'dark' ? 'white' : 'black'}
                        ariaLabel="grid-loading"
                        radius="12.5"
                        wrapperStyle={{}}
                        wrapperClass="grid-wrapper"
                        />
                        Retrieving records...
                    </span>
                </div>
            ) : data && data.length === 0 && (
                <div className="no-records-found">
                    <span>No records found</span>
                </div>
            )}
            
            <Modal
                open={groupInsightsModalOpen}
                onClose={handleCloseGroupInsightsModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                className="modal-global"
            >
                <Box className='modal-style' sx={ModalStyle}>
                    <div className="modal-header">
                        <h3><AutoAwesomeIcon /> AI Group Insights</h3>
                        <Button className="modal-btn" variant="contained" onClick={handleCloseGroupInsightsModal}>
                            Close
                        </Button>
                    </div>
                    <div id="modal-modal-description">
                        {!isGroupInsightsLoading && groupInsightsData ? (
                            <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={2} className="modal-summary-notes">
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card group-insights">
                                            <CardContent>
                                                <h4><BusinessIcon/>Competitor Mentioned</h4>
                                                {competitorMentionedData ? (
                                                    Array.isArray(competitorMentionedData) ? (
                                                        competitorMentionedData.map((value: any, index: number) => (
                                                            <li key={index}>{value}</li>
                                                        ))
                                                    ) : (
                                                        <span>{competitorMentionedData}</span>
                                                    )
                                                ) : (
                                                    <li>No data available</li>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card group-insights">
                                            <CardContent>
                                                <h4><CampaignIcon/>Promotion Mentioned</h4>
                                                {promotionsMentionedData ? (
                                                    Array.isArray(promotionsMentionedData) ? (
                                                        promotionsMentionedData.map((value: any, index: number) => (
                                                            <li key={index}>{value}</li>
                                                        ))
                                                    ) : (
                                                        <span>{promotionsMentionedData}</span>
                                                    )
                                                ) : (
                                                    <li>No data available</li>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card group-insights">
                                            <CardContent>
                                                <h4><SentimentVerySatisfiedIcon/>Customer's Reactions to Promotions</h4>
                                                {customerReactionData ? (
                                                    <span>{customerReactionData}</span>
                                                ) : (
                                                    <span>No data available</span>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card group-insights">
                                            <CardContent>
                                                <h4><SummarizeIcon/>General Summary</h4>
                                                {generalSummaryData ? (
                                                    <span>{generalSummaryData}</span>
                                                ) : (
                                                    <span>No data available</span>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <div className='position-reletive'>
                                <Skeleton width="100%" height="450px" />
                                <span className="centered-text">
                                    <GridLoading
                                    visible={true}
                                    height="15"
                                    width="15"
                                    color={mode === 'dark' ? 'white' : 'black'}
                                    ariaLabel="grid-loading"
                                    radius="12.5"
                                    wrapperStyle={{}}
                                    wrapperClass="grid-wrapper"
                                    />
                                    AI is generating group insights...
                                </span>
                            </div>
                        )}
                    </div>
                </Box>
            </Modal>

            {selectedTranscript[1].length > 0 && (
                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    className="modal-global"
                >
                    <Box className='modal-style' sx={TranscriptModalStyle}>
                        <div className="modal-header">
                            <h3><SummarizeIcon/> Full Transcript</h3>
                            <Button className="modal-btn" variant="contained" endIcon={<AutoAwesomeIcon />} onClick={() => {
                                handleInsightsButtonClick(selectedTranscript[2], selectedTranscript[3]);
                            }}>
                                AI Insights
                            </Button>
                        </div>
                        <Box sx={{ flexGrow: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={7.5} xl={7.5}>
                                    <Card variant="outlined" className="get-insights-card">
                                        <CardContent>
                                            <Typography id="modal-modal-description transcript-show-modal" sx={{ mt: 2 }}>                                            
                                            {selectedTranscript[1].map((segment, index) => (
                                            <React.Fragment key={index}>
                                                <Highlighter
                                                highlightClassName='highlighted-sentence'
                                                searchWords={highlightsData.flat()}
                                                autoEscape={true}
                                                caseSensitive={true}
                                                textToHighlight={segment}
                                                highlightTag={(props: { children: React.ReactNode }) => (
                                                    <Highlight
                                                        {...props}
                                                        highlightIndex={highlightsData.findIndex((highlight: string) =>
                                                            highlight.includes(props.children as string)
                                                        )}
                                                    />
                                                )}
                                                />
                                                <br />
                                                <br />
                                            </React.Fragment>
                                            ))}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={4.5} xl={4.5}>
                                    <Card variant="outlined" className="transcript-card transcript-highlighting highlight-legend">
                                        {highlightsData && highlightsData.length > 0 ? (
                                        <CardContent>
                                            <div className='legend-flex'>
                                                <div>
                                                    <p><SquareIcon className='color-alt-payment'/> Alternative Payments Asked</p>
                                                    <span>{(highlightsData[0][0] === 'NULL' || highlightsData[0][0] === undefined || highlightsData[0][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span>}</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-payment-promo-suggested'/> Payment Promotion Suggested</p>
                                                    <span>{(highlightsData[1][0] === 'NULL' || highlightsData[1][0] === undefined || highlightsData[1][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-promo-suggested'/> Transaction Promotion Suggested</p>
                                                    <span>{(highlightsData[2][0] === 'NULL' || highlightsData[2][0] === undefined || highlightsData[2][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                            </div>
                                            <Divider/>
                                        
                                            <div className='legend-flex'>
                                                <div>
                                                    <p><SquareIcon className='color-competitor' /> Competitor Usage Asked</p>
                                                    <span>{(highlightsData[3][0] === 'NULL' || highlightsData[3][0] === undefined || highlightsData[3][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-vendor-acceptance'/> Vendor Acceptance Asked</p>
                                                    <span>{(highlightsData[4][0] === 'NULL' || highlightsData[4][0] === undefined || highlightsData[4][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                                <div>
                                                    <p><SquareIcon className='color-send-profile'/> Spend Profile Discussed</p>
                                                    <span>{(highlightsData[5][0] === 'NULL' || highlightsData[5][0] === undefined || highlightsData[5][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span>}</span>
                                                </div>
                                            </div>
                                            <Divider/>
                                        
                                            <div className='legend-flex'>
                                                <div>
                                                    <p><SquareIcon className='color-data-history'/> Data History Mentioned</p>
                                                    <span>{(highlightsData[6][0] === 'NULL' || highlightsData[6][0] === undefined || highlightsData[6][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span>}</span>
                                                </div> 
                                                <div>
                                                    <p><SquareIcon className='color-rate-match'/> Rate Match Suggested</p>
                                                    <span>{(highlightsData[7][0] === '' || highlightsData[7][0] === undefined || highlightsData[7][0] === '') ? <span className='red-arrow'><CancelIcon/> Not detected</span> : <span className='green-arrow'><CheckCircleIcon/> Detected</span> }</span>
                                                </div>
                                            </div>                                        
                                        </CardContent>                                 
                                        ) : (
                                        <Skeleton width="100%" height="100%" />
                                        )}
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Modal>
            )}
            <Modal
                className="modal-global"
                open={insightsModalOpen}
                onClose={handleCloseInsightsModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box className='modal-style' sx={ModalStyle}>
                    <div className="modal-header">
                        <h3><AutoAwesomeIcon /> AI Insights</h3>
                        <Button className="modal-btn" variant="contained" onClick={handleCloseInsightsModal}>
                            Close
                        </Button>
                    </div>
                    <div id="modal-modal-description">
                        {!isInsightsDataLoading && insightsData ? (
                            <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={2} className="modal-summary-notes">
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><SentimentNeutralIcon/>Call Sentiment</h4>
                                                <span>{(insightsData && insightsData.Call_Sentiment !== '') ? insightsData.Call_Sentiment : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><CallIcon/>Call Type</h4>
                                                <span>{(insightsData && insightsData.Call_Type !== '') ? insightsData.Call_Type : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><CategoryIcon/>Product</h4>
                                                <span>{(insightsData && insightsData.Product !== '') ? insightsData.Product : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><ContactSupportIcon/>Call Reason</h4>
                                                <span>{(insightsData && insightsData.Reason !== '') ? insightsData.Reason : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><LoyaltyIcon/>Promotion Mentioned</h4>
                                                <span>{(insightsData && insightsData.Promotion_Mentioned !== '') ? insightsData.Promotion_Mentioned : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><SummarizeIcon/>Summary</h4>
                                                <span>{(insightsData && insightsData.Summary !== '') ? insightsData.Summary : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="summary-card">
                                        <Card variant="outlined" className="get-insights-card">
                                            <CardContent>
                                                <h4><AssistantPhotoIcon/>Next Steps</h4>
                                                <span>{(insightsData && insightsData.Next_Steps !== '') ? insightsData.Next_Steps : 'No data'}</span>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <div className='position-reletive'>
                                <Skeleton width="100%" height="450px" />
                                <span className="centered-text">
                                    <GridLoading
                                    visible={true}
                                    height="15"
                                    width="15"
                                    color={mode === 'dark' ? 'white' : 'black'}
                                    ariaLabel="grid-loading"
                                    radius="12.5"
                                    wrapperStyle={{}}
                                    wrapperClass="grid-wrapper"
                                    />
                                    AI is generating insights...
                                </span>
                            </div>
                        )}
                    </div>
                </Box>
            </Modal>
           
            <Modal className='exec-send-modal modal-global' open={isModalOpen} onClose={closeModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className='modal-style' sx={ModalStyle}>
                    <h3><SendIcon/> Report Form</h3>
                    <div>
                        <TextField
                        label="Subject"
                        variant="outlined"
                        fullWidth
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        margin="normal"
                        />
                        <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        margin="normal"
                        />
                        <TextField
                        label="Additional Comments"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        margin="normal"
                        />
                        <FormControl fullWidth className='send-report-dropdown'>
                            <InputLabel id="select-label">Report Frequency</InputLabel>
                            <Select
                                labelId="select-label"
                                id="select"
                                value={frequency}
                                onChange={(e) => {setFrequency(e.target.value);}}
                            >
                                <MenuItem value="Daily">Daily</MenuItem>
                                <MenuItem value="Weekly">Weekly</MenuItem>
                                <MenuItem value="Monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Num of Recurrences"
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={recurrences}
                            onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (!isNaN(value) && value >= 0 && value <= 365) {
                                    setRecurrences(value.toString());
                                } else if (e.target.value === '') {
                                    setRecurrences('');
                                }
                            }}
                            margin="normal"
                            inputProps={{ inputMode: 'numeric', max: 365 }}
                        />
                        <p className='text-dark-mode'>Recurrence Range:</p>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
                            <div className='send-report-datepickers'>
                                <DatePicker
                                    className='exec-date-picker'
                                    label="Start"
                                    value={rangeStartDate}
                                    onChange={(date) => {
                                        if (date && date.isBefore(Today, 'day')) {
                                            setRangeStartDate(Today);
                                        } else {
                                            setRangeStartDate(date || Today);
                                        }
                                    }}
                                />
                                <span className='text-dark-mode'> to </span>
                                <DatePicker
                                    className='exec-date-picker'
                                    label="End"
                                    value={rangeEndDate}
                                    onChange={(date) => {
                                        if (date && date.isBefore(Today, 'day')) {
                                            setRangeEndDate(Today);
                                        } else if (rangeStartDate && date && date.isBefore(rangeStartDate, 'day')) {
                                            setRangeEndDate(rangeStartDate);
                                        } else {
                                            setRangeEndDate(date || Today);
                                        }
                                    }}
                                />
                            </div>
                        </LocalizationProvider>
                        {excelFile && (
                            <div className="table-file-export">
                                <p onClick={handlePreview}>Preview Excel File</p>
                                {/* <input type="hidden" name="data" value={excelFile} /> */}
                            </div>
                        )}
                    </div>
                    <div>
                        <Button className='exec-send-btn' variant="contained" onClick={handleSendReport}>Send</Button>
                    </div>
                </Box>
            </Modal>
        </div>
    );
}

export default AgentEffectivenessSearch;
